import { FunctionComponent, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import backArrow from "../../assets/task/back_arrow.svg";
import { format } from "date-fns";
import { useLazyQuery, useMutation, ApolloError } from "@apollo/client";
import { EMPLOYEES_BY_AGENCY_ID, PODS_DATA, SCREENS_BY_PROJECT } from "../../api/query";
import { useAuth } from "../../context/AuthContext";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import deleteICon from "../../assets/delete-icon-b.svg";
import PlusIcon from "../../assets/task/plusicontask.svg";
import Loader from "../../components/common/Loader";
import { UPSERT_POD, DELETE_POD } from "../../api/mutation";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import BreadCumb from "../../components/common/Breadcrumb";

// ─── Screens Modal ────────────────────────────────────────────────────────────
interface ScreensModalProps {
  pod: any;
  screens: any[];
  onClose: () => void;
}

const ScreensModal: FunctionComponent<ScreensModalProps> = ({ pod, screens, onClose }) => {
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => searchRef.current?.focus(), 50);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return screens;
    return screens.filter((s) => s.name?.toLowerCase().includes(q));
  }, [screens, search]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-200 dark:border-slate-700 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white m-0">
              {pod.pod_name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 m-0">
              Total Screens: <span className="font-semibold text-main-blue">{screens.length}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 grid gap-3">
          <div className="grid grid-cols-2 mq1400:grid-2 mq1275:grid-cols-2 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full reportmulselect-dropdown">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search screens…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 border-[1px] border-solid border-surface-border dark:border-slate-600 rounded-radius-md text-sm bg-white dark:bg-slate-700 dark:text-white placeholder-gray-400 focus:outline-none focus:border-main-blue focus:ring-1 focus:ring-main-blue/20 transition-colors"
            />
          </div>
          <div className=" bg-white dark:dark:bg-slate-700 flex flex-col items-start justify-start gap-[24px] overflow-x-auto client-report-dtnamic-tbl max-h-[300px]">
            {filtered.length > 0 ? (
              <table className="table self-stretch border-[1px] border-solid border-surface-border rounded-radius-md overflow-x-auto dynamictabel-main-dv">
                <thead className="bg-surface-bg-secondary dark:bg-slate-600 sticky top-0">
                  <tr>
                    <th className="relative p-4 text-sm dark:text-white tdth-custom-width">
                      Screen Name
                    </th>
                    <th className="relative p-4 text-sm dark:text-white tdth-custom-width">
                      APIs
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filtered.map((screen: any, idx: number) => {
                    const apiCount = Array.isArray(screen.apis) ? screen.apis.length : 0;
                    return (
                      <tr key={screen.id ?? idx} className="hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                        <td className="tdth-custom-width dark:text-white text-sm ">{screen.name}</td>
                        <td className="tdth-custom-width dark:text-white text-sm ">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${apiCount > 0 ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-400"}`}>
                            {apiCount}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400 dark:text-gray-500">
                <svg className="w-10 h-10 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                </svg>
                <p className="text-sm">
                  {search.trim() ? "No screens match your search." : "No screens assigned to this pod."}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Table */}


        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-slate-700 shrink-0">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {filtered.length} of {screens.length} screen{screens.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={onClose}
            className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
          >
            <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
              Close
            </div>

          </button>
        </div>

      </div>
    </div>
  );
};

// ─── APIs Modal ───────────────────────────────────────────────────────────────
interface ApisModalProps {
  podName: string;
  apis: { screenName: string; api: any }[];
  onClose: () => void;
}

const ApisModal: FunctionComponent<ApisModalProps> = ({ podName, apis, onClose }) => (
  <div
    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-xl mx-4 flex flex-col max-h-[80vh] overflow-hidden">

      {/* Header */}
      <div className="flex items-start justify-between px-6 py-5 border-b border-gray-200 dark:border-slate-700 shrink-0">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white m-0">{podName}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 m-0">
            {apis.length} API{apis.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* API list */}
      <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
        {apis.map(({ screenName, api }, idx) => (
          <div
            key={api.id ?? idx}
            className="border border-[1px] border-solid border-surface-border dark:border-slate-600 rounded-lg p-4 flex flex-col gap-6 bg-gray-50 dark:bg-slate-700"
          >
            <div className="text-xs font-semibold text-main-blue uppercase tracking-wide">Screen Name : {screenName}</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="self-stretch relative font-semibold dark:text-white">API Name</label>
                <div className="text-sm font-medium text-gray-900 dark:text-white break-all">
                  {api.api_name || <span className="text-gray-400 italic">—</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="self-stretch relative font-semibold dark:text-white">Endpoint</label>
                <div className="text-sm font-mono text-violet-700 dark:text-violet-400 break-all">
                  {api.api_endpoint || <span className="text-gray-400 italic font-sans">—</span>}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="self-stretch relative font-semibold dark:text-white">Description</label>
              <textarea
                readOnly
                value={api.description || ""}
                rows={3}
                placeholder="No description"
                className="px-3 py-2 border border-[1px] border-solid border-surface-border  dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 dark:text-white placeholder-gray-400 resize-none focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-slate-700 shrink-0">
        <button
          onClick={onClose}
          className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
        >
          <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block"> Close</span>
        </button>
      </div>

    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const PodManagementPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const today = format(new Date(), "yyyy-MM-dd");
  const { userInfo } = useAuth();
  const agencyId = parseInt(userInfo?.agencies?.id);
  const organizationID = userInfo?.agencies?.id || "";
  const [newPodName, setNewPodName] = useState("");
  const [expandedPod, setExpandedPod] = useState<number | null>(null);
  const [podsData, setPodsData] = useState<any[]>([]);
  const [screensModalPod, setScreensModalPod] = useState<any | null>(null);
  const [apisModalPod, setApisModalPod] = useState<any | null>(null);

  const [assignedMemberOptions, setAssignedMemberOptions] = useState<
    { key: string; name: string }[]
  >([]);

  const [selectedEmployees, setSelectedEmployees] = useState<{
    [key: number]: { employeeID: string; employeeName: string };
  }>({});
  const [clearselected,setClearSelected] = useState(false)

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
  }>({ open: false, title: "", message: "", onConfirm: null });

  const [upsertPod, { loading: upsertLoading }] = useMutation(UPSERT_POD, { fetchPolicy: "no-cache" });
  const [deletePod, { loading: deletePodLoading }] = useMutation(DELETE_POD, { fetchPolicy: "no-cache" });

  const [getPodsData, { data: podsResponse, loading: podsLoading }] = useLazyQuery(PODS_DATA, { fetchPolicy: "no-cache" });
  const [getEmployeesByAgencyID, { data: employeesData, loading: employeesLoading }] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, { fetchPolicy: "no-cache" });
  const [getScreens1, { data: screensData1, loading: screensLoading1 }] = useLazyQuery(SCREENS_BY_PROJECT, { fetchPolicy: "no-cache" });
  const [getScreens2, { data: screensData2, loading: screensLoading2 }] = useLazyQuery(SCREENS_BY_PROJECT, { fetchPolicy: "no-cache" });

  useEffect(() => {
    if (!organizationID) return;
    getEmployeesByAgencyID({ variables: { agency_id: parseInt(organizationID) } });
    getPodsData({ variables: { agency_id: parseInt(organizationID), start_date: today, end_date: today } });
    getScreens1({ variables: { project_id: 1 } });
    getScreens2({ variables: { project_id: 4 } });
  }, [organizationID]);

  useEffect(() => {
    if (!employeesData?.employees_by_agency_id) return;
    setAssignedMemberOptions(
      employeesData.employees_by_agency_id.employees.map((emp: any) => ({
        key: emp.employee_id.toString(),
        name: `${emp.first_name} ${emp.last_name}`,
      })),
    );
  }, [employeesData]);

  useEffect(() => {
    if (!podsResponse?.pods_data?.data) return;
    setPodsData(podsResponse.pods_data.data.filter((it: any) => it.pod_name !== "All Pods"));
  }, [podsResponse]);

  // Group screens by pod_name once — no recalculation on every render
  const screensByPod = useMemo<Record<string, any[]>>(() => {
    const allScreens = [
      ...(screensData1?.screens_by_project ?? []),
      ...(screensData2?.screens_by_project ?? []),
    ];
    const map: Record<string, any[]> = {};
    allScreens.forEach((screen: any) => {
      const key = screen.pod_name || "";
      if (!map[key]) map[key] = [];
      map[key].push(screen);
    });
    return map;
  }, [screensData1, screensData2]);

  const openConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
  };

  const handleAccordion = (podID: number) => {
    setExpandedPod((prev) => (prev === podID ? null : podID));
  };

  const handleEmployeeSelect = (podID: number, employeeID: string, employeeName: string) => {
    setSelectedEmployees((prev) => ({ ...prev, [podID]: { employeeID, employeeName } }));
    setClearSelected(false)
  };

  const refreshPods = () => {
    getPodsData({ variables: { agency_id: parseInt(organizationID), start_date: today, end_date: today } });
  };

  const handleAddMember = async (podID: number) => {
    const employee = selectedEmployees[podID];
    if (!employee) return;
    try {
      const response = await upsertPod({
        variables: {
          input: { pod_name: null, pod_type: null, pod_id: podID, member_ids: [Number(employee.employeeID)], agency_id: agencyId },
        },
      });
      if (response.data.upsert_pod.status_code === 200) {
        showSuccess(response.data.upsert_pod.message);
        setSelectedEmployees((prev) => ({ ...prev, [podID]: { employeeID: "", employeeName: "" } }));
        refreshPods();
        setClearSelected(true)
      }
      if (response.data.upsert_pod.status_code === 400) showError(response.data.upsert_pod.message);
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  };

  const deleteMember = async (podID: number, employeeID: number) => {
    try {
      const response = await upsertPod({
        variables: {
          input: { pod_id: podID, pod_name: null, pod_type: null, member_ids: null, remove_member_ids: [employeeID], agency_id: agencyId },
        },
      });
      if (response.data.upsert_pod.status_code === 200) { showSuccess(response.data.upsert_pod.message); refreshPods(); }
      if (response.data.upsert_pod.status_code === 400) showError(response.data.upsert_pod.message);
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  };

  const deletePodAPI = async (podID: number) => {
    try {
      const response = await deletePod({ variables: { pod_id: podID } });
      if (response.data.delete_pod.status_code === 200) {
        showSuccess(response.data.delete_pod.message);
        if (expandedPod === podID) setExpandedPod(null);
        refreshPods();
      }
      if (response.data.delete_pod.status_code === 400) showError(response.data.delete_pod.message);
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  };

  const handleCreatePod = async () => {
    if (!newPodName.trim()) return;
    try {
      const response = await upsertPod({
        variables: {
          input: { pod_name: newPodName, pod_type: newPodName, member_ids: null, pod_id: null, agency_id: agencyId },
        },
      });
      if (response.data.upsert_pod.status_code === 201) { showSuccess(response.data.upsert_pod.message); setNewPodName(""); refreshPods(); }
      if (response.data.upsert_pod.status_code === 400) showError(response.data.upsert_pod.message);
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  };

  useEffect(() => {
  const isAnyModalOpen =
    screensModalPod !== null ||
    apisModalPod !== null ||
    confirmDialog.open;

  if (isAnyModalOpen) {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }

  return () => {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  };
}, [screensModalPod, apisModalPod, confirmDialog.open]);

  return (
    <>
      <div className="POD-management w-full relative overflow-hidden flex flex-col items-start justify-start min-w-[360px] max-w-[100%] leading-[normal] tracking-[normal] text-left text-xl text-colors-text-black font-body-xs-semi-bold mq450:max-w-full mq950:max-w-full mq1425:max-w-full mq1900:max-w-full">
        <div className="self-stretch bg-gray overflow-hidden flex flex-col items-center justify-start relative gap-[24px] min-h-[913px] max-w-full">
          <div className="w-full flex flex-col items-start justify-start py-40 px-20 mq1490:px-[30px] mq575:px-5 box-border gap-[24px] max-w-[100%]">
            <div className="grid gap-4">
               <BreadCumb />
            {/* Header */}
            <div className="self-stretch flex flex-row items-center justify-start gap-[5px] max-w-full">
              <img
                className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
                alt=""
                src={backArrow}
                onClick={() => navigate("/actions")}
              />
              <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
                POD Management
              </h3>
            </div>
            </div>

            <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">

              {/* Create Pod */}
              <div className="w-full">
                <h4 className="text-[16px] font-semibold mb-4">Create New Pod</h4>
                <div className="grid grid-cols-4 mq1425:grid-cols-4 mq900:grid-cols-1 mq575:grid-cols-1 flex-row flex-wrap items-center justify-start gap-[16px_14.7px] text-center w-full">
                 <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                   <input
                    type="text"
                    placeholder="Enter Pod Name"
                    value={newPodName}
                    onChange={(e) => setNewPodName(e.target.value)}
                    className="w-full [border:none] [outline:none] bg-[transparent] h-[19px] flex-1 flex flex-row items-center justify-start font-body-reg text-sm dark:text-white text-text-text-secondary undefined"
                  />
                 </div>
                </div>
                <div className="flex items-center justify-end mt-4">
                  <button
                    disabled={upsertLoading}
                    onClick={handleCreatePod}
                    className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                  >
                    <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block min-w-[32px]">Save</span>
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              {(() => {
                const allScreens = [
                  ...(screensData1?.screens_by_project ?? []),
                  ...(screensData2?.screens_by_project ?? []),
                ];
                const assignedScreens = allScreens.filter((s: any) => s.pod_name);
                const totalApis = assignedScreens.reduce((sum: number, s: any) => sum + (Array.isArray(s.apis) ? s.apis.length : 0), 0);
                const stats = [
                  { label: "Total Pods", value: podsData.length, color: "bg-blue-50 text-blue-700 border-blue-200" },
                  { label: "Total APIs", value: totalApis, color: "bg-violet-50 text-violet-700 border-violet-200" },
                  { label: "Total Screens", value: assignedScreens.length, color: "bg-emerald-50 text-emerald-700 border-emerald-200 darkgreen-item-dv" },
                ];
                return (
                  <div className="w-full grid grid-cols-3 mq575:grid-cols-1 gap-4">
                    {stats.map((s) => (
                      <div key={s.label} className={`flex flex-col items-center justify-center rounded-xl darkblue-bg-dv-item border px-6 py-4 ${s.color}`}>
                        <span className="text-3xl font-bold">{s.value}</span>
                        <span className="text-sm font-medium mt-1">{s.label}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Pods List */}
              <div className="w-full flex flex-col gap-4">
                {podsData.length > 0 ? (
                  podsData.map((pod: any) => {
                    const isExpanded = expandedPod === pod.pod_id;
                    const podScreens = screensByPod[pod.pod_name] ?? [];
                    const screenCount = podScreens.length;
                    const hasScreens = screenCount > 0;
                    const podApis = podScreens.flatMap((s: any) =>
                      Array.isArray(s.apis) ? s.apis.map((api: any) => ({ screenName: s.name, api })) : []
                    );
                    const apiCount = podApis.length;
                    const hasApis = apiCount > 0;

                    return (
                      <div
                        key={pod.pod_id}
                        className="border border-gray-300 rounded-xl overflow-hidden accorddion-header p-2 pt-0"
                      >
                        {/* Accordion Header */}
                        <div className="flex items-center justify-between px-4 py-0 cursor-pointer mq991:grid">
                          <div
                            className="flex items-center gap-3 flex-1 cursor-pointer"
                            onClick={() => handleAccordion(pod.pod_id)}
                          >
                            <span className="text-lg font-semibold">{isExpanded ? "▼" : "▶"}</span>
                            <h4 className="font-semibold text-[16px]">{pod.pod_name}</h4>
                          </div>

                          <div className="flex items-center gap-3">
                            {/* Members badge */}
                            <div className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium cursor-default">
                              {pod.members.length} Members
                            </div>

                            {/* APIs badge — clickable when count > 0 */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (hasApis) setApisModalPod({ pod, apis: podApis });
                              }}
                              disabled={!hasApis}
                              title={hasApis ? `View ${apiCount} API${apiCount !== 1 ? "s" : ""}` : "No APIs for this pod"}
                              className={`px-3 py-1 rounded-full text-sm font-medium border-none transition-colors
                                ${hasApis
                                  ? "bg-violet-100 text-violet-700 cursor-pointer hover:bg-violet-200"
                                  : "bg-gray-100 text-gray-400 cursor-default bggraylight-item-dv"
                                }`}
                            >
                              {apiCount} API{apiCount !== 1 ? "s" : ""}
                            </button>

                            {/* Screens badge — clickable when count > 0 */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (hasScreens) setScreensModalPod(pod);
                              }}
                              disabled={!hasScreens}
                              title={hasScreens ? `View ${screenCount} screen${screenCount !== 1 ? "s" : ""}` : "No screens assigned"}
                              className={`px-3 py-1 rounded-full text-sm font-medium border-none transition-colors
                                ${hasScreens
                                  ? "bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200"
                                  : "bg-gray-100 text-gray-400 cursor-default bggraylight-item-dv"
                                }`}
                            >
                              {screenCount} Screen{screenCount !== 1 ? "s" : ""}
                            </button>

                            {/* Delete pod */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openConfirmDialog(
                                  "Delete Pod",
                                  "Are you sure you want to delete this pod?",
                                  () => deletePodAPI(pod.pod_id),
                                );
                              }}
                              className="w-9 h-9 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center bg-transparent cursor-pointer"
                            >
                              <img src={deleteICon} alt="Delete" className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Accordion Body */}
                        {isExpanded && (
                          <div className="p-5 border-t bg-white border-gray-200">
                            <div className="flex flex-col gap-5">

                              {/* Add Member */}
                              <div className="flex items-end gap-3 mq750:flex-col mq750:items-stretch">
                                <div className="grid grid-cols-4 mq1425:grid-cols-4 mq900:grid-cols-1 mq575:grid-cols-1 flex-row flex-wrap items-center justify-start gap-[16px_14.7px] text-center w-full">
                                  <SearchableInputsDropdown
                                    label="Employee"
                                    placeHolder="Select Employee"
                                    initialValue={selectedEmployees[pod.pod_id]?.employeeName || ""}
                                    options={assignedMemberOptions}
                                    onSelect={(key: number, name: string) => {
                                      handleEmployeeSelect(pod.pod_id, key.toString(), name);
                                    }}
                                    clearSelected={clearselected}
                                  />
                                  <div className="flex items-center justify-start mq750:justify-start relative">
                                    <button
                                      className="absolute mq991:relative top-[-7px] min-h-[42px] h-[42px] cursor-pointer inline-block flex-col items-center justify-start px-[10px] py-[5px] plus-task-btn taskdash-plush-img rounded gap-1"
                                      onClick={() => handleAddMember(pod.pod_id)}
                                    >
                                      <img src={PlusIcon} alt="Add" className="w-5 h-5 relative top-[2px]" />
                                      <span className="text-sm text-white relative top-[-2px] dark:text-white">Add</span>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Members Grid */}
                              <div className="self-stretch grid grid-cols-4 mq1900:grid-cols-4 mq1425:grid-cols-4 __mq1275:grid-cols-3 mq950:grid-cols-2 mq825:grid-cols-1 mq575:grid-cols-1 flex-row flex-wrap items-start gap-[16px] justify-start">
                                {pod.members.length > 0 ? (
                                  pod.members.map((member: any, index: number) => (
                                    <div
                                      key={member.employee_id}
                                      className={`border-[1px] border-solid border-surface-border bg-white-item flex items-center justify-between px-5 py-4
                                        ${index !== pod.members.length - 1 ? "border-b border-gray-200" : ""}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-slate-700 flex items-center justify-center font-semibold text-blue-700">
                                          {member.first_name?.charAt(0)}
                                        </div>
                                        <span className="font-medium">{member.first_name} {member.last_name}</span>
                                      </div>
                                      <button
                                        onClick={() =>
                                          openConfirmDialog(
                                            "Remove Member",
                                            `Are you sure you want to remove ${member.first_name} ${member.last_name} from this pod?`,
                                            () => deleteMember(pod.pod_id, member.employee_id),
                                          )
                                        }
                                        className="text-red-600 font-medium flex responsive-btn-item removed-btn-pod hover:text-red-700 bg-transparent border-none cursor-pointer"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                                          <path d="M11.5 11.9998L4.5 4.99976" stroke="#FD605D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M11.5 4.99976L4.5 11.9998" stroke="#FD605D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))
                                ) : (
                                  <div className="py-8 text-center text-gray-500">No Members Assigned</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="self-stretch rounded-lg overflow-hidden flex flex-col items-center justify-center py-[100px] bg-slate-100">
                    <p>No Pods Available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {(upsertLoading || podsLoading || deletePodLoading || employeesLoading || screensLoading1 || screensLoading2) && <Loader />}

      {/* Screens Modal */}
      {screensModalPod && (
        <ScreensModal
          pod={screensModalPod}
          screens={screensByPod[screensModalPod.pod_name] ?? []}
          onClose={() => setScreensModalPod(null)}
        />
      )}

      {/* APIs Modal */}
      {apisModalPod && (
        <ApisModal
          podName={apisModalPod.pod.pod_name}
          apis={apisModalPod.apis}
          onClose={() => setApisModalPod(null)}
        />
      )}

      {/* Confirm Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="max-w-[420px] mq575:w-[300px] rounded-xl bg-white shadow-xl p-6 animate-fadeIn grid gap-6">
            <div className="flex flex-col gap-2 w-full">
              <h3 className="text-xl-dv font-semibold dark:text-white text-gray-900 my-0">{confirmDialog.title}</h3>
              <p className="my-0 dark:text-white text-gray-600">{confirmDialog.message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={closeConfirmDialog} className="px-5 py-2 rounded-lg border cta-red">
                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">Cancel</div>
              </button>
              <button
                onClick={() => { confirmDialog.onConfirm?.(); closeConfirmDialog(); }}
                className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
              >
                <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">Yes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PodManagementPage;
